import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Flag } from "lucide-react";
import Avatar from "@/components/Avatar";
import TaskSidePanel from "@/components/TaskSidePanel";
import { EmptyState } from "@/components/EmptyState";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, PRIORITY_ORDER, priorityOf, priorityRank } from "@/constants/status";
import { fmtDateTime } from "@/utils/date";
import { ClientTag } from "@/components/ClientTag";

const COLS = [
  { key: "title", label: "Client · Task" },
  { key: "status", label: "Status", w: "w-[150px]" },
  { key: "assignee", label: "Assignee", w: "w-[170px]" },
  { key: "client", label: "Project", w: "w-[200px]" },
  { key: "priority", label: "Priority", w: "w-[120px]" },
  { key: "due", label: "Due", w: "w-[150px]" },
];

const statusOf = (t, uid) => {
  const slot = (t.assignee_details || []).find((a) => a.user_id === uid);
  return (t.assignees || []).length > 1 && slot ? slot.status : t.status;
};

export default function ListView({ tasks, users, currentUser, isManager, onEdit, onStatusChange, onTogglePriority, onAdd }) {
  const [sort, setSort] = useState({ key: "due", dir: 1 });
  const [openId, setOpenId] = useState(null);
  const uid = currentUser?.id;
  const nameOf = (t) => (users.find((u) => u.id === t.assigned_to) || {}).name || t.assigned_to_name || "";

  const rows = useMemo(() => {
    const val = (t) => {
      switch (sort.key) {
        case "title": return (t.title || "").toLowerCase();
        case "status": return STATUS_ORDER.indexOf(statusOf(t, uid));
        case "assignee": return nameOf(t).toLowerCase();
        case "client": return `${t.client || ""} ${t.project_name || ""}`.toLowerCase();
        case "priority": return priorityRank(t);
        default: return t.deadline ? Date.parse(t.deadline) : Number.MAX_SAFE_INTEGER;
      }
    };
    return tasks.slice().sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, sort, uid, users]);

  const canEditTask = (t) => isManager || (t.assignees || []).includes(uid) || t.assigned_to === uid;
  const setStatus = (t, next) => {
    let reason = "";
    if (next === "incomplete") {
      reason = window.prompt("Reason for marking incomplete?", t.incomplete_reason || "");
      if (reason === null) return;
    }
    onStatusChange(t, next, { hold_reason: t.hold_reason || "", incomplete_reason: reason });
  };
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const open = rows.find((t) => t.id === openId);

  if (!tasks.length) {
    return <EmptyState text="No tasks match these filters." actionLabel="Add task" onAction={onAdd} testid="list-view-empty" />;
  }

  return (
    <div className="glass rounded-xl overflow-x-auto scrollbar-thin" data-testid="list-view">
      <table className="w-full text-sm min-w-[980px]">
        <thead>
          <tr className="border-b border-white/10 text-left">
            {COLS.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 font-medium text-xs text-white/55 ${c.w || ""}`}>
                <button type="button" onClick={() => toggleSort(c.key)} data-testid={`list-sort-${c.key}`}
                  className="inline-flex items-center gap-1 hover:text-white transition-colors">
                  {c.label}
                  {sort.key === c.key ? (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const st = statusOf(t, uid);
            const prio = priorityOf(t);
            const editable = canEditTask(t);
            const overdue = t.deadline && new Date(t.deadline) < new Date() && st !== "complete";
            return (
              <tr key={t.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`list-row-${t.id}`}>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => setOpenId(t.id)} data-testid={`list-open-${t.id}`}
                    className="font-semibold text-left hover:text-[#E11D2E] transition-colors line-clamp-1"><ClientTag client={t.client} />{t.title}</button>
                </td>
                <td className="px-3 py-2">
                  {editable ? (
                    <select value={st} onChange={(e) => setStatus(t, e.target.value)} data-testid={`list-status-${t.id}`}
                      className={`h-7 rounded-full px-2 text-xs font-medium bg-transparent ${STATUS_META[st]?.pill || ""}`}>
                      {STATUS_ORDER.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
                    </select>
                  ) : <span className={`text-xs px-2 py-1 rounded-full ${STATUS_META[st]?.pill || ""}`}>{STATUS_META[st]?.label}</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar user={{ id: t.assigned_to, name: nameOf(t) }} size={20} />
                    <span className="truncate text-white/80">{nameOf(t)}{(t.assignees || []).length > 1 ? ` +${t.assignees.length - 1}` : ""}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-white/60 truncate max-w-[200px]">{t.project_name || "—"}</td>
                <td className="px-3 py-2">
                  {isManager && onTogglePriority ? (
                    <div className="relative inline-flex items-center">
                      <Flag size={12} fill="currentColor" className={`absolute left-2 pointer-events-none ${PRIORITY_META[prio].text}`} />
                      <select value={prio} onChange={(e) => onTogglePriority(t, e.target.value)} data-testid={`list-priority-${t.id}`}
                        className="h-7 pl-6 pr-1 rounded-md border border-white/10 bg-transparent text-xs">
                        {PRIORITY_ORDER.map((k) => <option key={k} value={k}>{PRIORITY_META[k].label}</option>)}
                      </select>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs"><Flag size={12} fill="currentColor" className={PRIORITY_META[prio].text} />{PRIORITY_META[prio].label}</span>
                  )}
                </td>
                <td className={`px-3 py-2 text-xs font-mono ${overdue ? "text-red-500 font-semibold" : "text-white/60"}`}>
                  {t.deadline ? fmtDateTime(t.deadline) : "Up next"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {open && (
        <TaskSidePanel
          open={!!open}
          onOpenChange={(o) => !o && setOpenId(null)}
          task={open}
          status={statusOf(open, uid)}
          assignee={{ id: open.assigned_to, name: nameOf(open) }}
          currentUser={currentUser}
          isManager={isManager}
          canEdit={canEditTask(open)}
          onEdit={onEdit}
          onStatusChange={(next) => setStatus(open, next)}
          rawOnStatusChange={onStatusChange}
          onTogglePriority={onTogglePriority}
          onOpenComments={null}
        />
      )}
    </div>
  );
}
